<template>
  <v-dialog
    :value="value"
    max-width="960"
    scrollable
    @input="$emit('input', $event)"
    @click:outside="$emit('input', false)"
  >
    <v-card v-if="campo" class="profiling-card">
      <div class="profiling-hero">
        <div class="profiling-hero__copy">
          <div class="overline mb-1">Profiling do campo</div>
          <div class="text-h5 font-weight-medium">{{ titulo }}</div>
          <div class="profiling-hero__meta">
            <span>{{ campo.colunaOrigem }}</span>
            <span class="profiling-dot">·</span>
            <span>{{ campo.tipoDado || '—' }}</span>
            <span v-if="campo.isQuantidade" class="profiling-chip-mini">quantidade</span>
          </div>
        </div>
        <v-btn icon dark class="ml-2" @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="profiling-body">
        <template v-if="stats">
          <v-row dense class="mb-4">
            <v-col v-for="kpi in kpis" :key="kpi.label" cols="6" sm="4" md="3">
              <div class="kpi-tile">
                <div class="kpi-tile__label">{{ kpi.label }}</div>
                <div class="kpi-tile__value">{{ kpi.value }}</div>
                <div v-if="kpi.hint" class="kpi-tile__hint">{{ kpi.hint }}</div>
              </div>
            </v-col>
          </v-row>

          <v-row v-if="histogram.length" dense class="mb-3">
            <v-col cols="12">
              <div class="panel">
                <div class="panel__title">Distribuição</div>
                <div class="panel__subtitle">Histograma dos valores numéricos</div>
                <div class="hist">
                  <div
                    v-for="(bin, i) in histogram"
                    :key="i"
                    class="hist__col"
                  >
                    <div class="hist__count">{{ formatInt(bin.count) }}</div>
                    <div class="hist__track">
                      <div
                        class="hist__bar"
                        :style="{ height: barHeight(bin.count) }"
                      />
                    </div>
                    <div class="hist__label">{{ formatBin(bin) }}</div>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>

          <v-row v-if="topValues.length || samples.length" dense>
            <v-col v-if="topValues.length" cols="12" :md="samples.length ? 6 : 12">
              <div class="panel panel--stretch">
                <div class="panel__title">Valores mais frequentes</div>
                <div class="panel__subtitle">Top {{ topValues.length }} ocorrências</div>
                <div
                  v-for="(item, i) in topValues"
                  :key="i"
                  class="rank"
                >
                  <div class="rank__value" :title="String(item.value)">{{ item.value }}</div>
                  <div class="rank__track">
                    <div class="rank__bar" :style="{ width: rankWidth(item.count) }" />
                  </div>
                  <div class="rank__count">{{ formatInt(item.count) }}</div>
                </div>
              </div>
            </v-col>

            <v-col v-if="samples.length" cols="12" :md="topValues.length ? 6 : 12">
              <div class="panel panel--samples panel--stretch">
                <div class="panel__title">Amostras</div>
                <div class="panel__subtitle">Valores observados na base</div>
                <div class="sample-wrap">
                  <v-chip
                    v-for="(sample, i) in samples"
                    :key="i"
                    x-small
                    outlined
                    class="mr-1 mb-1"
                  >
                    {{ sample }}
                  </v-chip>
                </div>
              </div>
            </v-col>
          </v-row>
        </template>

        <v-alert v-else dense outlined type="info" color="primary" class="mt-2">
          O profiling deste campo ainda não está disponível. Aguarde o processamento ou envie o arquivo novamente.
        </v-alert>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-6">
        <v-spacer />
        <v-btn text color="primary" @click="$emit('input', false)">Fechar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  export default {
    name: 'DialogProfilingCampo',
    props: {
      value: { type: Boolean, default: false },
      campo: { type: Object, default: null }
    },
    computed: {
      titulo() {
        return this.campo?.nomeExibicao || this.campo?.colunaOrigem || 'Campo';
      },
      stats() {
        return this.normalize(this.campo?.profiling);
      },
      histogram() {
        return this.stats?.histogram || [];
      },
      topValues() {
        return this.stats?.topValues || [];
      },
      samples() {
        return (this.stats?.sampleValues || [])
          .filter((v) => v !== null && v !== undefined && v !== '')
          .slice(0, 12);
      },
      maxHist() {
        return Math.max(1, ...this.histogram.map((b) => Number(b.count) || 0));
      },
      maxTop() {
        return Math.max(1, ...this.topValues.map((b) => Number(b.count) || 0));
      },
      kpis() {
        const s = this.stats;
        if (!s) return [];
        const items = [
          { label: 'Únicos', value: this.formatInt(s.uniqueCount), hint: this.formatPct(s.uniquePct) + ' da base' },
          { label: 'Nulos', value: this.formatInt(s.nullCount), hint: this.formatPct(s.nullPct) },
          { label: 'Contém vazios', value: s.nullable ? 'Sim' : 'Não' }
        ];
        if (s.min != null || s.max != null) {
          items.push({ label: 'Mínimo', value: this.formatNum(s.min) });
          items.push({ label: 'Máximo', value: this.formatNum(s.max) });
        }
        if (s.mean != null) {
          items.push({ label: 'Média', value: this.formatNum(s.mean) });
        }
        if (s.stddev != null) {
          items.push({ label: 'Desvio padrão', value: this.formatNum(s.stddev) });
        }
        return items;
      }
    },
    methods: {
      normalize(raw) {
        if (!raw || typeof raw !== 'object') return null;
        return {
          nullable: !!raw.nullable,
          sampleValues: raw.sampleValues || raw.sample_values || [],
          min: raw.min ?? null,
          max: raw.max ?? null,
          mean: raw.mean ?? null,
          stddev: raw.stddev ?? null,
          nullCount: Number(raw.nullCount ?? raw.null_count ?? 0),
          nullPct: Number(raw.nullPct ?? raw.null_pct ?? 0),
          uniqueCount: Number(raw.uniqueCount ?? raw.unique_count ?? 0),
          uniquePct: Number(raw.uniquePct ?? raw.unique_pct ?? 0),
          topValues: (raw.topValues || raw.top_values || []).map((item) => ({
            value: item.value,
            count: Number(item.count) || 0
          })),
          histogram: (raw.histogram || []).map((bin) => ({
            start: bin.start,
            end: bin.end,
            count: Number(bin.count) || 0
          }))
        };
      },
      formatInt(n) {
        if (n == null || Number.isNaN(Number(n))) return '—';
        return Number(n).toLocaleString('pt-BR');
      },
      formatNum(n) {
        if (n == null || Number.isNaN(Number(n))) return '—';
        return Number(n).toLocaleString('pt-BR', { maximumFractionDigits: 2 });
      },
      formatPct(n) {
        if (n == null || Number.isNaN(Number(n))) return '—';
        const ratio = Number(n) <= 1 ? Number(n) : Number(n) / 100;
        return ratio.toLocaleString('pt-BR', { style: 'percent', maximumFractionDigits: 2 });
      },
      formatBin(bin) {
        const start = this.formatNum(bin.start);
        const end = this.formatNum(bin.end);
        return `${start}–${end}`;
      },
      barHeight(count) {
        return `${Math.max(6, (Number(count) / this.maxHist) * 100)}%`;
      },
      rankWidth(count) {
        return `${Math.max(4, (Number(count) / this.maxTop) * 100)}%`;
      }
    }
  };
</script>

<style scoped>
  .profiling-hero {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    background: #e5502c;
    color: #fff;
    padding: 20px 24px 18px;
  }

  .profiling-hero__copy .overline {
    letter-spacing: 0.12em;
    opacity: 0.85;
  }

  .profiling-hero__meta {
    margin-top: 6px;
    opacity: 0.9;
    font-size: 0.85rem;
  }

  .profiling-dot {
    margin: 0 6px;
  }

  .profiling-chip-mini {
    display: inline-block;
    margin-left: 8px;
    padding: 1px 8px;
    border: 1px solid rgba(255, 255, 255, 0.55);
    border-radius: 999px;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .profiling-body {
    background: #fafafa;
    padding: 20px 24px 8px;
  }

  .kpi-tile {
    background: #fff;
    border-radius: 8px;
    padding: 12px 14px;
    border-left: 3px solid #e5502c;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    min-height: 78px;
  }

  .kpi-tile__label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #888;
  }

  .kpi-tile__value {
    font-size: 1.25rem;
    font-weight: 600;
    color: #333;
    line-height: 1.3;
  }

  .kpi-tile__hint {
    font-size: 0.72rem;
    color: #999;
  }

  .panel {
    background: #fff;
    border-radius: 8px;
    padding: 16px 18px 12px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }

  .panel--stretch {
    height: 100%;
  }

  .panel--samples {
    padding: 10px 14px 8px;
  }

  .panel__title {
    font-weight: 600;
    color: #333;
  }

  .panel__subtitle {
    font-size: 0.75rem;
    color: #999;
    margin-bottom: 14px;
  }

  .hist {
    display: flex;
    align-items: flex-end;
    gap: 6px;
    min-height: 180px;
    padding-bottom: 4px;
  }

  .hist__col {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 0;
  }

  .hist__count {
    font-size: 0.85rem;
    font-weight: 600;
    color: #444;
    margin-bottom: 4px;
  }

  .hist__track {
    width: 100%;
    height: 120px;
    display: flex;
    align-items: flex-end;
    justify-content: center;
  }

  .hist__bar {
    width: 70%;
    max-width: 28px;
    background: #e5502c;
    border-radius: 4px 4px 0 0;
  }

  .hist__label {
    margin-top: 6px;
    font-size: 0.8rem;
    color: #555;
    text-align: center;
    line-height: 1.25;
    word-break: break-word;
  }

  .rank {
    display: grid;
    grid-template-columns: minmax(48px, 28%) 1fr auto;
    gap: 10px;
    align-items: center;
    margin-bottom: 8px;
  }

  .rank__value {
    font-size: 0.8rem;
    color: #444;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .rank__track {
    height: 8px;
    background: #f0f0f0;
    border-radius: 999px;
    overflow: hidden;
  }

  .rank__bar {
    height: 100%;
    background: #e5502c;
    border-radius: 999px;
  }

  .rank__count {
    font-size: 0.75rem;
    color: #666;
    min-width: 42px;
    text-align: right;
  }

  .sample-wrap {
    margin-top: 6px;
    line-height: 1.2;
  }
</style>
